import { Suspense, lazy } from 'react';
import { Outlet } from 'react-router-dom';
import { AuthGuard, PermissionGuard } from 'src/auth/guard';
import DashboardLayout from 'src/layouts/dashboard';
import { LoadingScreen } from 'src/components/loading-screen';

const HomePage = lazy(() => import('src/pages/home'));
const DashboardPage = lazy(() => import('src/pages/app/dashboard'));
const EmployeesPage = lazy(() => import('src/pages/app/employees'));
const SpecialtiesPage = lazy(() => import('src/pages/app/specialties'));
const QuestionsPage = lazy(() => import('src/pages/app/questions'));
const InstructionsPage = lazy(() => import('src/pages/app/instructions'));
const ResultsPage = lazy(() => import('src/pages/app/results'));
const ResultDetailPage = lazy(() => import('src/pages/app/result-detail'));
const MedicalPage = lazy(() => import('src/pages/app/medical'));
const MedicalCreatePage = lazy(() => import('src/pages/app/medical-create'));
const MedicalDetailPage = lazy(() => import('src/pages/app/medical-detail'));
const MedicalEditPage = lazy(() => import('src/pages/app/medical-edit'));
const TestingPage = lazy(() => import('src/pages/app/testing'));
const TestingQuestionsPage = lazy(() => import('src/pages/app/testing-questions'));
const LocomotivesPage = lazy(() => import('src/pages/app/locomotives'));
const LocomotivesNewPage = lazy(() => import('src/pages/app/locomotives-new'));
const LocomotivesEditPage = lazy(() => import('src/pages/app/locomotives-edit'));
const LocomotivesDetailPage = lazy(() => import('src/pages/app/locomotives-detail'));
const WheelMeasurementsPage = lazy(() => import('src/pages/app/wheel-measurements'));
const WheelMeasurementsNewPage = lazy(() => import('src/pages/app/wheel-measurements-new'));
const WheelMeasurementsEditPage = lazy(() => import('src/pages/app/wheel-measurements-edit'));
const ActivityReportPage = lazy(() => import('src/pages/app/activity-report'));
const MyResultDetailPage = lazy(() => import('src/pages/app/my-result-detail'));

export const dashboardRoutes = [
  {
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { path: 'home', element: <HomePage /> },
      {
        path: 'dashboard',
        element: (
          <PermissionGuard page="dashboard" action="read">
            <DashboardPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'employees',
        element: (
          <PermissionGuard page="employees" action="read">
            <EmployeesPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'specialties',
        element: (
          <PermissionGuard page="specialties" action="read">
            <SpecialtiesPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'questions',
        element: (
          <PermissionGuard page="questions" action="read">
            <QuestionsPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'instructions',
        element: (
          <PermissionGuard page="instructions" action="read">
            <InstructionsPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'results',
        element: (
          <PermissionGuard page="results" action="read">
            <ResultsPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'results/:id',
        element: (
          <PermissionGuard page="results" action="detail">
            <ResultDetailPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'activity-report',
        element: (
          <PermissionGuard page="activity_report" action="read">
            <ActivityReportPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'medical',
        element: (
          <PermissionGuard page="medical" action="read">
            <MedicalPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'medical/new/:employeeId',
        element: (
          <PermissionGuard page="medical" action="write">
            <MedicalCreatePage />
          </PermissionGuard>
        ),
      },
      {
        path: 'medical/:id',
        element: (
          <PermissionGuard page="medical" action="detail">
            <MedicalDetailPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'medical/:id/edit',
        element: (
          <PermissionGuard page="medical" action="write">
            <MedicalEditPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'testing',
        element: (
          <PermissionGuard page="testing" action="write">
            <TestingPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'testing/tests/:specialistId/:module',
        element: (
          <PermissionGuard page="testing" action="write">
            <TestingQuestionsPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'my-results/:attemptId',
        element: <MyResultDetailPage />,
      },
      {
        path: 'locomotives',
        element: (
          <PermissionGuard page="wheel_measurements" action="read">
            <LocomotivesPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'locomotives/new',
        element: (
          <PermissionGuard page="wheel_measurements" action="write">
            <LocomotivesNewPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'locomotives/:id/edit',
        element: (
          <PermissionGuard page="wheel_measurements" action="write">
            <LocomotivesEditPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'locomotives/:id',
        element: (
          <PermissionGuard page="wheel_measurements" action="read">
            <LocomotivesDetailPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'wheel-measurements',
        element: (
          <PermissionGuard page="wheel_measurements" action="read">
            <WheelMeasurementsPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'wheel-measurements/new',
        element: (
          <PermissionGuard page="wheel_measurements" action="write">
            <WheelMeasurementsNewPage />
          </PermissionGuard>
        ),
      },
      {
        path: 'wheel-measurements/:id/edit',
        element: (
          <PermissionGuard page="wheel_measurements" action="write">
            <WheelMeasurementsEditPage />
          </PermissionGuard>
        ),
      },
    ],
  },
];
